import type { ChangeLogResourceType } from '@query/changeLog'
import type { TFunction } from 'i18next'

/** Temporary label before API has returned a localized type_label for this slug. */
export const humanizeResourceTypeSlug = (slug: ChangeLogResourceType): string => {
  const [first, ...rest] = slug.split('_')
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
}

/**
 * Exact stored change-log values → i18n keys.
 * Prefer longer/more specific tokens; lookup is exact-match first.
 */
const EXACT_ENUM_LABEL_KEYS: Record<string, string> = {
  // Document relations
  ACCRUAL_PAIR: 'documentRelations.types.ACCRUAL_PAIR',
  SALARY_PAYMENT: 'documentRelations.types.SALARY_PAYMENT',
  ACCRUAL: 'documentRelations.roles.ACCRUAL.label',
  SETTLEMENT: 'documentRelations.roles.SETTLEMENT.label',

  // Business form
  FI_OY: 'constants.businessForm.FI_OY',
  FI_OYJ: 'constants.businessForm.FI_OYJ',
  FI_TMI: 'constants.businessForm.FI_TMI',
  FI_KY: 'constants.businessForm.FI_KY',
  FI_OK: 'constants.businessForm.FI_OK',
  FI_AY: 'constants.businessForm.FI_AY',
  FI_AS_OY: 'constants.businessForm.FI_AS_OY',
  FI_KOY: 'constants.businessForm.FI_KOY',
  FI_NY: 'constants.businessForm.FI_NY',
  FI_YHD: 'constants.businessForm.FI_YHD',
  DE_UG: 'constants.businessForm.DE_UG',
  DE_GMBH: 'constants.businessForm.DE_GMBH',
  DEMO: 'constants.businessForm.DEMO',

  // VAT posting / reporting
  NET: 'settings.automation.defaultAccounts.vatPostingMethod.net',
  SPLIT: 'settings.automation.defaultAccounts.vatPostingMethod.split',
  DEFAULT: 'vat_reporting_method.default.label',
  DE_CASH_BASIS: 'vat_reporting_method.de_cash_basis.label',

  // Invoice status
  DRAFT: 'invoicing.status.draft',
  ACCEPTED: 'invoicing.status.accepted',
  PAID: 'invoicing.status.paid',
  CREDIT_LOSS: 'invoicing.status.creditLoss',

  // Delivery methods
  EMAIL_XRECHNUNG: 'invoicing.deliveryMethod.EMAIL_XRECHNUNG',
  EINVOICE: 'invoicing.deliveryMethod.EINVOICE',
  EMAIL: 'invoicing.deliveryMethod.EMAIL',
  PAPER: 'invoicing.deliveryMethod.PAPER',
  ELASKU: 'invoicing.deliveryMethod.ELASKU',
  SELF_PRINT: 'invoicing.deliveryMethod.SELF_PRINT',

  // Invoice recurrence reference method
  KEEP: 'constants.invoiceRepetition.referenceMethods.KEEP',
  ROLL: 'constants.invoiceRepetition.referenceMethods.ROLL',

  // Plans
  FREE: 'constants.nocfoPlan.FREE',
  PLUS: 'constants.nocfoPlan.PLUS',
  PRO: 'constants.nocfoPlan.PRO',
  ENTERPRISE: 'constants.nocfoPlan.ENTERPRISE',

  // Plan source / import / debt collection (change-log specific keys)
  STRIPE: 'settings.changeLog.enums.planSource.STRIPE',
  MANUAL: 'settings.changeLog.enums.planSource.MANUAL',
  AGENCY: 'settings.changeLog.enums.planSource.AGENCY',
  APIX: 'settings.changeLog.enums.importSource.APIX',
  UPLOAD: 'settings.changeLog.enums.importSource.UPLOAD',
  VAT_PURCHASE_INVOICE: 'settings.changeLog.enums.importSource.VAT_PURCHASE_INVOICE',
  NOT_STARTED: 'settings.changeLog.enums.debtCollection.NOT_STARTED',
  EMAIL_SENT: 'settings.changeLog.enums.debtCollection.EMAIL_SENT',
  SENT_TO_KRAVIA: 'settings.changeLog.enums.debtCollection.SENT_TO_KRAVIA',
  FAILED: 'settings.changeLog.enums.debtCollection.FAILED'
}

const VAT_PERIOD_LABEL_KEYS: Record<string, string> = {
  '-1': 'constants.vatPeriod.none',
  '0': 'constants.vatPeriod.month',
  '1': 'constants.vatPeriod.quarter',
  '2': 'constants.vatPeriod.year'
}

const FIELD_VALUE_LABEL_KEYS: Record<string, Record<string, string>> = {
  default_vat_period: VAT_PERIOD_LABEL_KEYS,
  period: VAT_PERIOD_LABEL_KEYS
}

/** Longer tokens first so EMAIL_XRECHNUNG / ACCRUAL_PAIR win over EMAIL / ACCRUAL. */
const EMBEDDED_ENUM_TOKENS = Object.keys(EXACT_ENUM_LABEL_KEYS).sort((a, b) => b.length - a.length)

const translateExact = (value: string, t: TFunction, field?: string): string | null => {
  const fieldMap = field ? FIELD_VALUE_LABEL_KEYS[field] : undefined
  if (fieldMap?.[value]) {
    return t(fieldMap[value])
  }
  const key = EXACT_ENUM_LABEL_KEYS[value]
  return key ? t(key) : null
}

const humanizeEmbedded = (value: string, t: TFunction): string => {
  let result = value
  for (const token of EMBEDDED_ENUM_TOKENS) {
    if (!result.includes(token)) continue
    const label = t(EXACT_ENUM_LABEL_KEYS[token])
    // Only replace whole tokens bordered by non-identifier chars.
    result = result.replace(new RegExp(`(?<![A-Z0-9_])${token}(?![A-Z0-9_])`, 'g'), label)
  }
  return result
}

export const humanizeChangeLogValue = (
  value: string | null,
  t: TFunction,
  field?: string
): string | null => {
  if (value == null) return null

  const exact = translateExact(value, t, field)
  if (exact != null) return exact

  if (value.includes(' → ')) {
    return value
      .split(' → ')
      .map(part => humanizeChangeLogValue(part, t, field) ?? part)
      .join(' → ')
  }

  if (value.includes(', ')) {
    return value
      .split(', ')
      .map(part => humanizeChangeLogValue(part, t, field) ?? part)
      .join(', ')
  }

  return humanizeEmbedded(value, t)
}
